import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router';
import { logger } from '../utils/logger';
import { signOut } from 'aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';
import Logo from './Logo';
import NotificationBell from './NotificationBell';
import PersistentPlayer from './PersistentPlayer';
import PersistentBroadcaster from './PersistentBroadcaster';
import './Layout.css';

const Layout: React.FC = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  // Hide persistent player on stream detail pages
  const isStreamDetailPage = /^\/streams\/[^/]+$/.test(location.pathname);

  return (
    <div className="layout">
      <PersistentBroadcaster />
      <header className="header">
        <div className="header-content">
          <Link to="/" className="logo-link">
            <Logo size="md" variant="full" />
          </Link>

          <nav className="nav-menu">
            <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>
              Home
            </Link>
            <Link to="/streams" className={`nav-link ${isActive('/streams') ? 'active' : ''}`}>
              Streams
            </Link>
            <Link to="/live" className={`nav-link ${location.pathname.startsWith('/live') ? 'active' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ marginRight: '6px' }}>
                <circle cx="8" cy="8" r="6"/>
                <circle cx="8" cy="8" r="2" fill="white"/>
              </svg>
              Live
            </Link>

            {/* No theme toggle: Radio ships one committed dark world (TEM-1195). */}
            {user && <NotificationBell />}

            {user ? (
              <>
                <Link to="/activity" className={`nav-link ${isActive('/activity') ? 'active' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '5px' }}>
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                  Feed
                </Link>
                <Link to="/analytics" className={`nav-link nav-link-analytics ${isActive('/analytics') ? 'active' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '5px' }}>
                    <path d="M18 20V10M12 20V4M6 20v-6"/>
                  </svg>
                  Analytics
                </Link>
                <div className="nav-dropdown">
                  <button className="nav-link nav-dropdown-trigger">
                    Creator
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" style={{ marginLeft: '4px' }}>
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                  </button>
                  <div className="nav-dropdown-menu">
                    <Link to="/dashboard" className="dropdown-item">Dashboard</Link>
                    <Link to="/manage-streams" className="dropdown-item">Manage Streams</Link>
                    <Link to="/go-live" className="dropdown-item">Go Live</Link>
                    <div className="dropdown-divider" />
                    <Link to="/profile" className="dropdown-item">Profile</Link>
                    <Link to="/favorites" className="dropdown-item">Favorites</Link>
                    <Link to="/history" className="dropdown-item">Listen History</Link>
                    <div className="dropdown-divider" />
                    <Link to="/settings" className="dropdown-item">Settings</Link>
                    <button onClick={handleSignOut} className="dropdown-item dropdown-item-button">
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm">
                Sign In
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="main-content">
        <Outlet />
      </main>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <Logo size="sm" variant="icon" />
            <p className="footer-text">
              &copy; 2025 Tempest Radio. Broadcasting excellence.
            </p>
          </div>
          <div className="footer-links">
            <Link to="/about" className="footer-link">About</Link>
            <Link to="/privacy" className="footer-link">Privacy</Link>
            <Link to="/terms" className="footer-link">Terms</Link>
            <a href="mailto:info@thetempestradio.com" className="footer-link">Contact</a>
          </div>
        </div>
      </footer>

      {!isStreamDetailPage && <PersistentPlayer />}
    </div>
  );
};

export default Layout;
